import { Grid, Typography } from '@mui/material';
import { MprcCard, MprcContainer, MprcTypography } from 'components';
import { DiscussionListFilter, EnrollmentComponentDto, TrainingComponentType } from "generated/CoreApi_v1";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from "react-router";
import { NIL } from 'uuid';
import { AssessmentContent } from "views/lms/student/EnrollmentView/Course/ContentComponents/assessment/AssessmentContent";
import { TrainingAssessmentHeaderContent } from 'views/lms/student/EnrollmentView/Course/ContentComponents/assessment/TrainingAssessmentHeaderContent';
import { MeetingContent } from "views/lms/student/EnrollmentView/Course/ContentComponents/meeting/MeetingContent";
import { MeetingHeaderContent } from 'views/lms/student/EnrollmentView/Course/ContentComponents/meeting/MeetingHeaderContent';
import { ScoContent } from 'views/lms/student/EnrollmentView/Course/ContentComponents/sco/ScoContent';
import { ScoContentHeader } from 'views/lms/student/EnrollmentView/Course/ContentComponents/sco/ScoContentHeader';
import { CourseEnrollmentViewHeader } from 'views/lms/student/EnrollmentView/Course/CourseEnrollmentViewHeader';
import PreviewNotice from 'views/lms/student/EnrollmentView/Shared/PreviewNotice';
import { useEnrollmentViewContext } from '..';
import { launchSco } from '../Shared/EnrollmentActions';
import { EnrollmentViewAbout, EnrollmentViewAboutTabs, isOfTypeEnrollmentViewAboutTabs, } from "../Shared/EnrollmentViewAbout";
import { EnrollmentViewContainer } from "../Shared/EnrollmentViewContainer";
import { ActivityContent } from "./ContentComponents/activity/ActivityContent";
import { PostContent } from './ContentComponents/post/PostContent';
import { CourseEnrollmentViewRightPanel } from './CourseEnrollmentViewRightPanel';
import { CourseEnrollmentViewSteps } from "./CourseEnrollmentViewSteps";
import { useEnrollmentViewTimer } from "./useEnrollmentViewTimer";

//////////////////////////////////////////////////////

type CourseEnrollmentViewContextProps = {

    currentComponent?: EnrollmentComponentDto,
    setCurrentComponent: (component: EnrollmentComponentDto) => void;
    setCurrentComponentId: (componentId: string) => void;

    hasNext: () => boolean;
    onNextModule: () => void;
};

const CourseEnrollmentViewContext = React.createContext<CourseEnrollmentViewContextProps>(undefined!);
export const useCourseEnrollmentViewContext = () => React.useContext(CourseEnrollmentViewContext);

//////////////////////////////////////////////////////

export const CourseEnrollmentView: React.FC = () => {

    const api = useCoreApi();

    const [searchParams, setSearchParams] = useSearchParams();
    const { enrollment, onReload } = useEnrollmentViewContext();

    const [currentComponent, setCurrentComponent] = useState<EnrollmentComponentDto>();
    const ignoreTimerFocusedWindow = useRef<boolean>(false);
    const ignoreTimerWindowActivity = useRef<boolean>(false);
    const initialAboutTab = useRef<EnrollmentViewAboutTabs>("overview");
    const initialDiscussionFilter = useRef<DiscussionListFilter>(DiscussionListFilter.All);

    const registerViewTimeSeconds = 30;

    const enableHeader = enrollment.course?.singleComponent == false ||
        (
            currentComponent?.componentType != TrainingComponentType.Evaluation &&
            currentComponent?.componentType != TrainingComponentType.Meeting &&
            currentComponent?.componentType != TrainingComponentType.Sco
        );


    /////////////////////////////////////////////////////////////
    /// Controle do tempo de visualização
    /////////////////////////////////////////////////////////////


    const { enableTimer, disableTimer } = useEnrollmentViewTimer(
        registerViewTimeSeconds
        , registerViewTime
        , () => ignoreTimerFocusedWindow.current
        , () => ignoreTimerWindowActivity.current
    );

    ///
    function registerViewTime() {
        api.v1.enrollment.registerTimeSpent({
            enrollmentId: enrollment.enrollmentId,
            seconds: registerViewTimeSeconds
        }).catch((e) => {
            console.error(e);
        });
    }

    ///
    const handleAttachOpen = () => {
        ignoreTimerFocusedWindow.current = true;
    };

    ///
    const handleAttachClose = () => {
        ignoreTimerFocusedWindow.current = false;
    };

    /////////////////////////////////////////////////////////////
    /// Controle de visualização dos Módulos
    /////////////////////////////////////////////////////////////

    function findNextComponentFilter(item: EnrollmentComponentDto, index: number, currentComponentIndex: number) {
        return index > currentComponentIndex
            && item.completed === false
            && item.enabled
            && (
                (item.componentType != TrainingComponentType.Phase && item.componentType != TrainingComponentType.Sco)
                ||
                (item.componentType == TrainingComponentType.Sco && item.scoComponent!.isPage)
            );
    }

    ///
    const getNextModuleIndex = useCallback(() => {
        if (enrollment != undefined) {
            const currentComponentIndex = enrollment.course?.components.findIndex(p => p.componentId === currentComponent?.componentId) ?? -1;
            if (currentComponentIndex != -1) {
                var nextComponentIndex = enrollment.course?.components.findIndex((item, i) => findNextComponentFilter(item, i, currentComponentIndex));
                return nextComponentIndex ?? -1;
            }
        }
        return -1;
    }, [enrollment, currentComponent]);

    ///
    const nextModule = useCallback(() => {
        const nextModuleIndex = getNextModuleIndex();
        if (nextModuleIndex != -1) {
            setCurrentComponent(enrollment!.course!.components[nextModuleIndex]);

            var component = enrollment!.course!.components[nextModuleIndex];
            //Se for Sco, inicia o curso imediatamente.
            if (component.componentType == TrainingComponentType.Sco) launchSco(enrollment.enrollmentId, component.scoComponent!, onReload, enrollment.readOnly);
        }
    }, [enrollment, getNextModuleIndex, launchSco]);

    //////////////////////////////////////////////////////
    /// EFFECT'S
    //////////////////////////////////////////////////////

    useEffect(() => {
        if (currentComponent && enrollment.enrollmentId != NIL) {

            api.v1.enrollment.addComponentAccess({ enrollmentId: enrollment!.enrollmentId, componentId: currentComponent.componentId });

            if (currentComponent.componentType == TrainingComponentType.Sco) {
                // Tempo do SCO é computado diretamento no backend.
                disableTimer();
            }
            else {
                enableTimer();

                // Reseta variáveis de controle.
                ignoreTimerFocusedWindow.current = false;

                ignoreTimerWindowActivity.current =
                    currentComponent.componentType == TrainingComponentType.Post
                    ||
                    currentComponent.componentType == TrainingComponentType.Evaluation;
            }
        }
    }, [currentComponent]);


    /*************************************************************************************
     * Analisa QueryString
     * "c" = Componente
     * "at" = Overview About Tab
     * "df" = Discussion Filter
    *************************************************************************************/
    useEffect(() => {
        if (enrollment.course != null) {
            const lastComponentAccessed = enrollment.course.currentComponentId;

            const component =
                enrollment.course.components.find(p => p.componentId == searchParams.get("c") && p.enabled == true) ||
                enrollment.course.components.find(p => p.componentId == lastComponentAccessed && p.enabled == true);

            setCurrentComponent(component);
        }

        if (searchParams.size > 0) {
            setSearchParams("", { replace: true });
        }
    }, [enrollment]);

    // Este check ficou fora o useEffect propositalmente, para que não seja necessário criar um estado.
    if (searchParams.has("at")) {
        const at = searchParams.get("at");
        if (at && isOfTypeEnrollmentViewAboutTabs(at)) initialAboutTab.current = searchParams.get("at") as EnrollmentViewAboutTabs;
    }
    if (searchParams.has("df")) {
        const df = searchParams.get("df");
        if (df) {
            const filter: DiscussionListFilter = (DiscussionListFilter[df as any]) as any;
            if (filter) initialDiscussionFilter.current = filter;
        }
    }

    //////////////////////////////////////////////////////
    /// RENDER
    //////////////////////////////////////////////////////

    const RenderComponentContent = () => {

        const firstComponent = enrollment.course!.components.length > 0 ? enrollment.course!.components[0] : null;
        if (!currentComponent || firstComponent == null || !enableHeader)
            return null;

        if (currentComponent.componentType == TrainingComponentType.Post)
            return <PostContent
                enrollmentId={enrollment.enrollmentId}
                componentId={currentComponent.componentId}
                onComplete={onReload}
                onAttachOpen={handleAttachOpen}
                onAttachClose={handleAttachClose}
                readOnly={enrollment.readOnly || enrollment.enrollmentId == NIL}
            />;

        if (currentComponent.componentType == TrainingComponentType.Activity)
            return <ActivityContent
                enrollmentId={enrollment.enrollmentId}
                componentId={currentComponent.componentId}
                onComplete={onReload}
                onAttachOpen={handleAttachOpen}
                onAttachClose={handleAttachClose}
                readOnly={enrollment.readOnly}
                isMultipleComponentsView={(enrollment.course?.components.length ?? 0) > 1}
            />;

        if (currentComponent.componentType == TrainingComponentType.Evaluation)
            return <AssessmentContent
                enrollmentId={enrollment.enrollmentId}
                componentId={currentComponent.componentId}
                onComplete={onReload}
            />;

        if (currentComponent.componentType == TrainingComponentType.Meeting)
            return <MeetingContent
                enrollmentId={enrollment.enrollmentId}
                componentId={currentComponent.componentId}
                classroomId={enrollment.classroomId}
            />;

        if (currentComponent.componentType == TrainingComponentType.Sco)
            return <ScoContent />;

        return null;
    };

    return (
        <CourseEnrollmentViewContext.Provider
            value={
                {
                    hasNext: () => getNextModuleIndex() != -1,
                    currentComponent,
                    setCurrentComponent,
                    setCurrentComponentId: (componentId: string) => setCurrentComponent(enrollment.course!.components.find(p => p.componentId == componentId)),
                    onNextModule: nextModule
                }
            }
        >
            <MprcContainer>
                {enableHeader && <CourseEnrollmentViewHeader />}
            </MprcContainer>
            {enrollment.isPreview && <PreviewNotice />}
            <EnrollmentViewContainer
                content={
                    <Grid container direction="row">
                        <Grid size={{ xs: 12 }}>
                            {!enableHeader &&
                                <MprcContainer>
                                    <MprcCard>
                                        <Grid container direction="row" width="100%" p={1}>
                                            <Grid size={{ xs: 12 }}>
                                                <MprcTypography variant="h5" color="black" component="div" noWrap>{enrollment.trainingName}</MprcTypography>
                                                {enrollment.multipleClasses && <Typography variant="caption" color="muted" component="p" noWrap>{enrollment.classroomName}</Typography>}
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                {
                                                    (currentComponent?.assessmentComponent && currentComponent?.componentType == TrainingComponentType.Evaluation)
                                                        ? <TrainingAssessmentHeaderContent assessment={currentComponent.assessmentComponent} />
                                                        : (currentComponent?.componentType == TrainingComponentType.Meeting)
                                                            ? <MeetingHeaderContent componentId={currentComponent.componentId} />
                                                            : (currentComponent?.componentType == TrainingComponentType.Sco)
                                                                ? <ScoContentHeader />
                                                                : <></>
                                                }
                                            </Grid>
                                        </Grid>
                                    </MprcCard>
                                </MprcContainer>
                            }
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            {RenderComponentContent()}
                        </Grid>
                        {
                            enrollment.course!.components.length > 1 &&
                            <Grid size={{ xs: 12 }}>
                                <CourseEnrollmentViewSteps variant="horizontal" />
                            </Grid>
                        }
                        <Grid size={{ xs: 12 }}>
                            <MprcContainer>
                                <EnrollmentViewAbout
                                    openOnTab={initialAboutTab.current}
                                    discussionFilter={initialDiscussionFilter.current}
                                />
                            </MprcContainer>
                        </Grid>
                    </Grid>
                }
                rightPanel={<CourseEnrollmentViewRightPanel />}
            />
        </CourseEnrollmentViewContext.Provider>
    );
};
